import { useMutation } from '@tanstack/react-query';

import { tokenStorage } from '@/config/tokenStorage';
import { tokenRefresher } from '@/lib/auth/TokenRefresher';

import { getRestApiBaseUrlWithVersion } from '../getRestApiBaseUrlWithVersion';
import { replacePathVariables } from './requests/utils/replacePathVariables';

export const useApiStreamPostRequest = <TData, TVariables>(
  path: string,
  options?: {
    onChunk?: (chunk: TData) => void;
  },
) => {
  return useMutation({
    mutationFn: async ({
      pathVariables,
      queryParams,
      body,
      signal,
    }: {
      pathVariables?: Record<string, unknown>;
      queryParams?: Record<string, string>;
      body: TVariables;
      signal?: AbortSignal;
    }) => {
      await tokenRefresher.checkToken();
      const authToken = tokenStorage.getItem('authToken');

      const baseUrl = getRestApiBaseUrlWithVersion();

      const formattedPathVariables = replacePathVariables(path, pathVariables);
      const formattedQueryParams = new URLSearchParams(queryParams).toString();
      const url = `${baseUrl}/${formattedPathVariables}/?${formattedQueryParams}`;

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify(body),
        signal,
      });

      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const reader = response.body?.getReader();
      if (!reader) throw new Error('No reader available');

      const decoder = new TextDecoder();
      let buffer = '';

      reader.read().then(function processChunk({ done, value }): Promise<void> {
        if (done) return Promise.resolve();

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';
        lines.forEach((line) => {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));
              options?.onChunk?.(data);
            } catch (e) {
              throw new Error(`Failed to parse SSE data: ${e}`);
            }
          }
        });
        return reader.read().then(processChunk);
      });

      return { success: true };
    },
  });
};
